// src/pages/Home.js
import _ from "lodash";
import moment from "moment";
import React, { useEffect, useState, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { NativeEventSource, EventSourcePolyfill } from "event-source-polyfill";

import ReCAPTCHA from "react-google-recaptcha";

import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay, Navigation, Pagination } from "swiper/modules";
import "swiper/css";
import "swiper/css/pagination";
import "swiper/css/navigation";

import SEO from "components/common/SEO";
import Section from "components/common/Section";
import CardBox2 from "components/common/CardBox2";
import HeadlessNews from "components/common/HeadlessNews";
import FormBotProtection from "components/common/FormBotProtection";

import "styles/home.scss";

import {
  Search,
  Globe,
  Robot,
  JournalBookmark,
  CreditCard,
  Clipboard2Check,
  CardList,
  ShieldLock,
  Radar,
  Activity,
} from "react-bootstrap-icons";

import ReactApexChart from "react-apexcharts";
import FadeLoader from "react-spinners/FadeLoader";
import { Formik, Form, Field } from "formik";
import * as Yup from "yup";
import { useSnackbar } from "notistack";

import {
  setAPICallCount,
  setIsCaptchaRequired,
  setCaptchaVerified,
} from "store/slices/rateLimitSlice";

import { getHomeChartOption } from "utils/chartOptions";
import { getStoredData as _getStoredData } from "services/CommonService";
import {
  getStoredData,
  fetchStatsData,
  fetchNewsData,
  errorHandler,
} from "services/HomeService";
import {
  sendEmail,
  checkPrivateEmail,
  formatNumber,
  createAntiBotInitialValues,
  getAntiBotPayload,
} from "utils/global";

const VALIDATION_SERVER_URL = process.env.REACT_APP_VALIDATION_SERVER_URL;
const VALIDATION_SERVER_API_KEY =
  process.env.REACT_APP_VALIDATION_SERVER_API_KEY;

const EventSource = EventSourcePolyfill || NativeEventSource;

function Home() {
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const gartnerIframeRef = useRef(null);

  useEffect(() => {
    const href = window.location.href.substring(
      window.location.href.lastIndexOf("#") + 1
    );
    const element = document.getElementById(href);
    if (element) {
      element.scrollIntoView({ /*behavior: 'smooth',*/ inline: "center" });
    }
  }, []);

  const [leaks, setLeaks] = useState(null);
  const [accounts, setAccounts] = useState(null);
  const [allStats, setAllStats] = useState(null);
  const [latestLeaks, setLatestLeaks] = useState(null);
  const [loadingStatsData, setLoadingStatsData] = useState(false);

  useEffect(() => {
    const getStatsData = async () => {
      setLoadingStatsData(true);

      const cachedData = getStoredData("stats-data");
      if (!_.isNull(cachedData)) {
        setLeaks(cachedData.leaks);
        setAccounts(cachedData.accounts);
        setAllStats(cachedData.all);
        setLatestLeaks(cachedData.latest_leaks.content);
      } else {
        try {
          const result = await fetchStatsData();

          setLeaks(result.leaks);
          setAccounts(result.accounts);
          setAllStats(result.all);
          setLatestLeaks(result.latest_leaks.content);
        } catch (error) {
          errorHandler(error, enqueueSnackbar, [429]);
        }
      }
      setLoadingStatsData(false);
    };

    getStatsData();
  }, []);

  const [latestNews, setLatestNews] = useState(null);

  useEffect(() => {
    const getNews = async () => {
      const cachedData = _getStoredData("latest-news");
      if (!_.isNull(cachedData)) {
        setLatestNews(cachedData);
      } else {
        try {
          const result = await fetchNewsData();
          setLatestNews(result);
        } catch (error) {
          console.error(error);
        }
      }
    };

    getNews();
  }, []);

  const formatChartLeaks = () => {
    if (_.isNull(leaks)) return null;

    return {
      series: [
        {
          name: "Leaks",
          data: leaks.map((item) => item.leaks),
        },
      ],
      options: getHomeChartOption("Leaks", "#14B00D", leaks),
    };
  };

  const formatChartAccounts = () => {
    if (_.isNull(accounts)) return null;

    return {
      series: [
        {
          name: "Accounts",
          data: accounts.map((acc) => acc.accounts),
        },
      ],
      options: getHomeChartOption("Accounts", "#CC0F52", accounts),
    };
  };

  const [currentPage, setCurrentPage] = useState(1);
  const rowsPerPage = 4,
    totalPages = 5;

  const goToPage = (pageNumber) => {
    if (pageNumber >= 1 && pageNumber <= totalPages) {
      setCurrentPage(pageNumber);
    }
  };

  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [resultCount, setResultCount] = useState(0);
  const [eventSource, setEventSource] = useState(null);
  const [searchStatus, setSearchStatus] = useState("idle");
  const [searchedDomain, setSearchedDomain] = useState("");

  const closeSearchStream = (source) => {
    if (!source) return;

    source.onmessage = null;
    source.onerror = null;
    source.close();
    setEventSource((currentSource) =>
      currentSource === source ? null : currentSource
    );
  };

  const startSearch = (domain) => {
    if (eventSource) {
      closeSearchStream(eventSource);
    }

    const trimmedDomain = domain.trim().toLowerCase();
    let latestTotalCount = 0;
    let latestProgress = 0;
    let hasReceivedSearchUpdate = false;
    let hasCompletedSearch = false;

    const finalizeSearch = (totalCount) => {
      hasCompletedSearch = true;
      setResultCount(totalCount);
      setSearchStatus(totalCount > 0 ? "results" : "empty");
      setLoading(false);
      closeSearchStream(newEventSource);
    };

    const newEventSource = new EventSource(
      `${VALIDATION_SERVER_URL}/get_leaks_count/?domain=${encodeURIComponent(
        trimmedDomain
      )}`,
      {
        headers: {
          Authorization: `Api-Key ${VALIDATION_SERVER_API_KEY}`,
        },
        heartbeatTimeout: 300000,
      }
    );

    setLoading(true);
    setProgress(0);
    setResultCount(0);
    setSearchedDomain(trimmedDomain);
    setSearchStatus("loading");

    newEventSource.onmessage = (event) => {
      const data = JSON.parse(event.data);
      const totalCount = Number(data.total_count) || 0;
      const progressValue = Number(data.progress) || 0;

      latestTotalCount = totalCount;
      latestProgress = progressValue;
      hasReceivedSearchUpdate = true;

      switch (data.status) {
        case "processing":
          setProgress(progressValue);
          setResultCount(totalCount);
          break;
        case "complete":
        case "completed":
        case "done":
        case "success":
          finalizeSearch(totalCount);
          break;
        case "error":
          hasCompletedSearch = true;
          setLoading(false);
          setSearchStatus("error");
          enqueueSnackbar(data.message, {
            variant: "error",
            anchorOrigin: { horizontal: "right", vertical: "top" },
          });
          closeSearchStream(newEventSource);
          break;
        default:
      }
    };

    newEventSource.onerror = (err) => {
      if (hasCompletedSearch) {
        return;
      }

      if (hasReceivedSearchUpdate && latestProgress >= 100) {
        finalizeSearch(latestTotalCount);
        return;
      }

      setLoading(false);
      setSearchStatus("error");

      console.error("SSE connection error:", err);

      errorHandler(err, enqueueSnackbar);

      closeSearchStream(newEventSource);
    };

    // Save the new EventSource connection
    setEventSource(newEventSource);
  };

  // Cleanup when the component is unmounted
  useEffect(() => {
    return () => {
      if (eventSource) {
        closeSearchStream(eventSource);
      }
    };
  }, [eventSource]);

  const { apiCallCount, isCaptchaRequired, captchaVerified } = useSelector(
    (state) => state.rateLimit
  );
  const callLimit = 2; // Limit: 10 API calls
  const resetInterval = 60000; // 1 minute (in milliseconds)

  const timerRef = useRef(null);

  // Reset the API call counter every minute
  useEffect(() => {
    timerRef.current = setInterval(() => {
      dispatch(setAPICallCount(0));
    }, resetInterval);

    return () => clearInterval(timerRef.current);
  }, []);

  // Handle ReCAPTCHA verification
  const handleCaptchaVerify = (token) => {
    if (token) {
      dispatch(setCaptchaVerified(true));
      dispatch(setIsCaptchaRequired(false));
      dispatch(setAPICallCount(0)); // Reset API call count after verification
    }
  };

  const handleIframeLoad = () => {
    const iframeContent = gartnerIframeRef.current.contentDocument || gartnerIframeRef.current.contentWindow.document;
    const contentHeight = iframeContent.body.scrollHeight; const contentWidth = iframeContent.body.scrollWidth;
    gartnerIframeRef.current.style.height = `${contentHeight}px`;
    gartnerIframeRef.current.style.width = `${contentWidth}px`;
  }

  return (
    <div className="">
      <SEO />

      {/* hero */}

      <Section style="banner">
        <div className="grid grid-cols-12 items-center px-4 lg:px-20">
          <div className="col-span-12 lg:col-span-8">
            <h1>
              THE DARKNET
              <br />
              <span>OWNS YOUR Data?</span>
            </h1>
            <h3 className="flex pb-2">
              INVESTIGATE NOW HERE{" "}
              <img
                src={require("assets/images/home/arrow.png")}
                className="img-fluid ml-3 mt-2"
                alt=""
              />
            </h3>

            <Formik
              initialValues={{
                domain: "",
              }}
              validationSchema={Yup.object().shape({
                domain: Yup.string()
                  .max(253)
                  .matches(
                    /^(?!https?:\/\/)[a-zA-Z0-9-]+\.[a-zA-Z]{2,}(\.[a-zA-Z]{2})?$/, // Domain validation
                    "Invalid domain format"
                  )
                  .required("Domain is required"),
              })}
              onSubmit={(values) => {
                // If ReCAPTCHA is required and not verified, block search
                if (isCaptchaRequired && !captchaVerified) {
                  console.warn("ReCAPTCHA required and not verified.");
                  return;
                }

                if (apiCallCount >= callLimit) {
                  dispatch(setIsCaptchaRequired(true)); // Require ReCAPTCHA after hitting the limit
                  dispatch(setCaptchaVerified(false));
                  console.warn("API call limit exceeded. ReCAPTCHA triggered.");
                  return;
                }

                // Rate-limited API call
                dispatch(setAPICallCount(apiCallCount + 1));

                // enqueueSnackbar(
                //   'Sorry, we are currently undergoing server maintenance, so you cannot use the search function.',
                //   {
                //     variant: 'error',
                //     anchorOrigin: { horizontal: 'right', vertical: 'top' },
                //     style: {
                //       width: 450,
                //     },
                //   },
                // );

                // return;

                // Start search
                startSearch(values.domain);
              }}
            >
              {({ errors, handleChange, handleSubmit, values }) => (
                <form className="pb-4" onSubmit={handleSubmit}>
                  <div className="form">
                    <input
                      type="text"
                      name="domain"
                      placeholder="Your domain (example.com)"
                      value={values.domain}
                      // required
                      onChange={(e) => {
                        if (searchStatus !== "idle") {
                          setSearchStatus("idle");
                          setResultCount(0);
                          setSearchedDomain("");
                        }

                        handleChange(e);
                      }}
                      onKeyUp={(e) => {
                        if (e.key === "Enter") handleSubmit();
                      }}
                      disabled={isCaptchaRequired && !captchaVerified}
                    />
                    <button
                      type="submit"
                      disabled={isCaptchaRequired && !captchaVerified}
                      className={
                        isCaptchaRequired && !captchaVerified ? "disabled" : ""
                      }
                    >
                      <div className="flex items-center">
                        <span className="me-2">
                          <Search />
                        </span>{" "}
                        Search
                      </div>
                    </button>

                    <div
                      className="loading text-gray-50"
                      style={{ display: loading ? "flex" : "none" }}
                    >
                      <FadeLoader
                        color="white"
                        height={10}
                        margin={-6}
                        radius={4}
                        width={4}
                        cssOverride={{ top: 21 }}
                      />
                      Please wait...{" "}
                      {progress > 0 && <span>&#40;{progress}%&#41;</span>}
                    </div>
                  </div>

                  {errors.domain ? (
                    <p className="text-sm text-red-700 pl-2 pt-1">
                      {errors.domain}
                    </p>
                  ) : (
                    <></>
                  )}
                </form>
              )}
            </Formik>

            <p className="hero-supporting-copy">
              Real-time threat intelligence powered by leaked credentials, dark
              web monitoring, and attack surface enumeration.
            </p>

            {/* ReCAPTCHA */}
            {isCaptchaRequired && !captchaVerified && (
              <div className="mt-4">
                <ReCAPTCHA
                  sitekey="6LdTv4IqAAAAAPW_nCUMGgdLgRzi1irKvakEv3wA"
                  onChange={handleCaptchaVerify}
                />
              </div>
            )}

            {(searchStatus === "empty" ||
              (!isCaptchaRequired && searchStatus === "results")) ? (
              <div
                className={`search-feedback ${
                  searchStatus === "results"
                    ? "search-feedback-results"
                    : "search-feedback-empty"
                }`}
                role="status"
                aria-live="polite"
              >
                {searchStatus === "results" ? (
                  <p>
                    <strong>{resultCount.toLocaleString()}</strong> results found
                    {searchedDomain ? ` for ${searchedDomain}` : ""}.{" "}
                    <a href="/free-trial">Start a free trial</a> to review the
                    exposed data in the platform.
                  </p>
                ) : (
                  <p>
                    <strong>Good news:</strong> no leaks found
                    {searchedDomain ? ` for ${searchedDomain}` : ""}. We did not
                    identify any exposed data for this domain. Try another
                    domain, or <a href="/free-trial">start a free trial</a> to
                    monitor your assets continuously.
                  </p>
                )}
              </div>
            ) : null}
          </div>
          <div className="col-span-12 lg:col-span-4 flex !items-end app-intro h-full">
            <div className="app-intro-card">
              <div>
                <div className="app-intro-card__kicker">Why DarknetSearch?</div>
                <h2 className="app-intro-card__title">
                  Real <span>Threat Intelligence</span>
                </h2>
                <div className="app-intro-card__list">
                  {heroFeatureItems.map(({ icon: Icon, text }) => (
                    <div className="app-intro-card__item" key={text}>
                      <span className="app-intro-card__icon" aria-hidden="true">
                        <Icon size={12} />
                      </span>
                      <span>{text}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div className="app-intro-card__footer">
                <a href="/pricing" className="app-intro-card__badge">
                  <ShieldLock size={11} aria-hidden="true" />
                  Trusted by MSSPs, SOCs & Enterprises
                </a>
                <div className="app-intro-card__made-in">
                  Made in <span>Switzerland</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Section>

      <Section style="latest-news mx-4 lg:mx-20">
        <div className="grid grid-cols-12">
          <div className="col-span-12">
            <div className="d-flex">
              <div className="box">
                <img
                  src={require("assets/images/home/latest-news-icon.png")}
                  className="img-fluid"
                  alt=""
                />
                <h3>LATEST NEWS</h3>
              </div>
              <div className="line"></div>
              <div className="latest-news-content">
                {!_.isNull(latestNews) && latestNews?.date ? (
                  <div className="latest-news-date">
                    {moment(latestNews.date).format("MMM D, YYYY")}
                  </div>
                ) : null}
                <a
                  href={
                    !_.isNull(latestNews)
                      ? `/knowledge/news/en/${latestNews && latestNews.slug}`
                      : "#"
                  }
                  target="_self"
                >
                  {!_.isNull(latestNews) ? latestNews && latestNews.title && latestNews.title.rendered : ""}
                  <i className="bi bi-chevron-right"></i>
                </a>
              </div>
            </div>
          </div>
        </div>
      </Section>

      <Section style="threat">
        <div className="px-4 lg:px-20 mx-auto d-block">
          <h6 className="uppercase">Darkweb Monitoring</h6>
          <h2>
            Discover an <span>Effective and Affordable</span> <br /> Darknet
            Threat Intelligence Solution
          </h2>
          <p>
            Our dark web and deep web monitoring continuously tracks
            confidential data across dark web markets, telegram channels, paste
            sites, botnet logs, IRC, social media and other sources. We alert
            you when your information is exposed. Know what <a className="primary-color" aria-label="hackers" href="https://darknetsearch.com/glossary/what-is-a-hacker">hackers</a> know and
            stop cyber attacks before they target your organization.
          </p>
        </div>
        <div className="grid grid-cols-12 d-flex flex flex-col items-center px-4 lg:px-20">
          <div className="col-span-12 lg:col-span-5 pe-4">
            <img
              src={require("assets/images/home/threat-intelligence.png")}
              className="img-fluid mainImg"
              alt=""
            />
          </div>
          <div className="col-span-12 lg:col-span-6 pe-4">
            <div className="box">
              <div className="d-flex" style={{ marginTop: 0 }}>
                <h5>
                  <b>Leak Detection:</b> Identify stolen data, compromised
                  devices, and supply chain threats in real time
                </h5>
              </div>
              <div className="d-flex d-flex2">
                <h5>
                  <b>Attack Surface Discovery:</b> See your network like an
                  attacker, revealing CVEs, open ports, and shadow IT
                </h5>
              </div>
              <div className="d-flex d-flex3">
                <h5>
                  <b>Domain & Brand Protection:</b> Prevent brand spoofing and
                  <a className="primary-color" aria-label="phishing" href="https://darknetsearch.com/glossary/what-is-a-phishing-attack"> phishing</a> by monitoring typo-squatted domains
                </h5>
              </div>
            </div>
          </div>
          <div className="col-span-12 lg:col-span-1 flex lg:block justify-center">
            {ratings.map((rating, index) => (
              <a
                key={index}
                href={rating.link}
                target="_blank"
                className="rating-link"
                rel="noopener noreferrer"
              >
                <img
                  src={require(`assets/images/${rating.image}`)}
                  className="img-fluid kadu"
                  alt={rating.title}
                />
              </a>
            ))}
          </div>
        </div>
      </Section>

      <Section style="free-trial-cta threat-visibility-cta px-4 lg:px-20">
        <div className="grid grid-cols-12">
          <div className="col-span-12">
            <a
              className="free-trial-banner"
              href="https://darknetsearch.com/free-trial"
            >
              <div className="label">
                <span className="label-icon" aria-hidden="true">
                  <Radar size={24} />
                </span>
                <h3>THREAT VISIBILITY</h3>
              </div>
              <div className="line"></div>
              <div className="cta-text cta-text-column">
                <h4>Continuous Threat Visibility</h4>
                <p>
                  Monitor leaks, stealer logs, attack surface, and threat
                  chatter - all in one platform.
                </p>
              </div>
              <div className="free-trial-btn">Explore the Platform</div>
            </a>
          </div>
        </div>
      </Section>

      <img src={require("assets/images/curve.png")} className="w-full" alt="" />

      <Section style="fslider">
        <div className="grid grid-cols-12 items-center px-0 lg:px-20">
          <div className="col-span-12">
            <div className="z-[11]">
              <Swiper
                slidesPerView={1}
                spaceBetween={20}
                modules={[Navigation]}
                navigation={{ clickable: true }}
                loop
                cssMode={true}
                breakpoints={{
                  576: {
                    slidesPerView: 1,
                    spaceBetween: 20,
                  },
                  768: {
                    slidesPerView: 2,
                    spaceBetween: 40,
                  },
                  1024: {
                    slidesPerView: 4,
                    spaceBetween: 50,
                  },
                }}
              >
                {cardInfos.map((item, index) => (
                  <SwiperSlide key={index}>
                    <CardBox2
                      title={item.title}
                      image={item.image}
                      imageTitle={item.imageTitle}
                      link={item.link}
                      style={{ tablet: 2, laptop: 4 }}
                      limit={15}
                    >
                      {item.text}
                    </CardBox2>
                  </SwiperSlide>
                ))}
              </Swiper>
            </div>
          </div>
        </div>
        <div className="grid grid-cols-12 items-center px-4 lg:px-20">
          <div className="col-span-12">
            <div className="trust relative">
              <h4>Trusted By</h4>
              <Swiper
                slidesPerView={1}
                spaceBetween={20}
                cssMode={true}
                navigation={{ clickable: true }}
                modules={[Autoplay, Navigation]}
                loop
                autoplay={{
                  delay: 5000,
                  stopOnLastSlide: false,
                }}
                breakpoints={{
                  576: {
                    slidesPerView: 1,
                    spaceBetween: 20,
                  },
                  768: {
                    slidesPerView: 2,
                    spaceBetween: 40,
                  },
                  1024: {
                    slidesPerView: 4,
                    spaceBetween: 50,
                  },
                }}
              >
                {trusts.map((item, index) => (
                  <SwiperSlide key={index}>
                    <div className="imgbox">
                      <a href={item.link} target="_blank" rel="noopener">
                        <img
                          src={require(`assets/images/${item.image}`)}
                          className={`img-fluid ${
                            item.matchHeight ? "trusted-logo--match-height" : ""
                          }`}
                          alt=""
                        />
                      </a>
                    </div>
                  </SwiperSlide>
                ))}
              </Swiper>
            </div>
          </div>
        </div>
      </Section>

      <Section style="threat hidden xl:flex my-10 reviews">
        <h2 className="pb-5">
          Customer <span>Success Stories</span>
        </h2>
        <div>
          <iframe id='testimonialto-949104b1-3f55-4eef-bafa-472aaf694f86' src="https://embed-v2.testimonial.to/carousel/all/gartner?id=949104b1-3f55-4eef-bafa-472aaf694f86" frameborder="0" scrolling="no" width="100%" height="500" ></iframe>
        </div>
        <div className="global-btn">
          <a href="/customer-success-stories">See More</a>
        </div>
      </Section>

      <Section style="use-cases-home">
        <div className="grid grid-cols-12 gap-8 px-4 lg:px-20">
          <div className="col-span-12 lg:col-span-4 use-cases-intro">
            <span className="eyebrow">Use Cases</span>
            <h2>
              Built For <span>Every Role</span>
              <br />
              In Cybersecurity
            </h2>
            <p>
              DarknetSearch adapts to how different teams work. From executive
              visibility to tactical response, each role gets targeted insights
              and actionable alerts.
            </p>
            <a className="use-cases-cta" href="/use-cases">
              Explore all use cases
            </a>
            <div className="use-cases-visual">
              <img
                src={require("assets/images/ciso-soc.png")}
                alt="DarknetSearch analysts collaborating in the SOC"
                loading="lazy"
              />
            </div>
          </div>

          <div className="col-span-12 lg:col-span-8">
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6 use-cases-grid">
              {homeUseCases.map((item) => (
                <div key={item.title} className="use-case-card">
                  <div className="card-head">
                    <span className="role-pill">{item.role}</span>
                    <div className="icon">{item.icon}</div>
                  </div>
                  <h3>{item.title}</h3>
                  <p>
                    <strong>Key Challenge:</strong> {item.challenge}
                  </p>
                  <p>
                    <strong>Use Case:</strong> {item.useCase}
                  </p>
                  <p className="keywords">
                    <strong>Keywords:</strong> {item.keywords}
                  </p>
                  {item.outcome && (
                    <p className="outcome">
                      <strong>Outcome:</strong> {item.outcome}
                    </p>
                  )}
                  <div className="card-actions">
                    {item.cta && (
                      <a
                        className="cta-button"
                        href={item.cta.href}
                        target="_blank"
                        rel="noreferrer"
                      >
                        {item.cta.label}
                      </a>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </Section>

      <Section style="free-trial-cta px-4 lg:px-20">
        <div className="grid grid-cols-12">
          <div className="col-span-12">
            <a className="free-trial-banner" href="/pricing">
              <div className="label">
                <img
                  src={require("assets/images/home/latest-news-icon.png")}
                  className="img-fluid"
                  alt=""
                />
                <h3>REQUEST A QUOTE</h3>
              </div>
              <div className="line"></div>
              <div className="cta-text">
                Get a tailored pricing proposal based on your organization&apos;s
                needs and risk profile.
                <i className="bi bi-chevron-right"></i>
              </div>
              <div className="free-trial-btn">REQUEST YOUR QUOTE NOW</div>
            </a>
          </div>
        </div>
      </Section>

      <Section style="testimonials">
        <div className="grid grid-cols-12 gap-y-10 px-4 lg:px-20">
          <div className="col-span-12 text-center">
            <h2 className="leading-tight">
              What Our <span>Clients Say</span>
            </h2>
            <p className="testimonial-lead">
              Hear from security leaders who trust Kaduu to protect their
              organizations from credential theft and cyber threats.
            </p>
          </div>
          <div className="col-span-12">
            <Swiper
              slidesPerView={1}
              spaceBetween={30}
              modules={[Navigation, Pagination, Autoplay]}
              navigation
              pagination={{ clickable: true }}
              autoplay={{ delay: 8000, disableOnInteraction: false }}
              loop
              className="testimonial-swiper"
            >
                {homeTestimonials.map((item, index) => (
                  <SwiperSlide key={index}>
                    <div className="testimonial-card">
                      <div className="testimonial-logo">
                        {item.link ? (
                          <a
                            href={item.link}
                            rel="noreferrer"
                            aria-label={item.name}
                          >
                            <img
                              src={require(`assets/images/${item.logo}`)}
                              alt={item.company}
                              className="img-fluid"
                            />
                          </a>
                        ) : (
                          <img
                            src={require(`assets/images/${item.logo}`)}
                            alt={item.company}
                            className="img-fluid"
                          />
                        )}
                      </div>
                      <p className="quote">
                        <span>&#8220;</span>
                        {item.text}
                        <span>&#8221;</span>
                      </p>
                      <div className="divider"></div>
                      <div className="author">
                        <h4>
                          {item.name ? (
                          <a href={item.link} rel="noreferrer">
                            {item.name}
                          </a>
                            ) : (
                              item.name
                            )}
                        </h4>
                        <h6>{item.title}</h6>
                        <p>
                            {item.company}
                        </p>
                      </div>
                    </div>
                  </SwiperSlide>
                ))}
            </Swiper>
          </div>
        </div>
      </Section>

      <Section style="darknet">
        <div className="grid grid-cols-3 lg:grid-flow-col gap-4 items-center px-0 lg:px-20">
          {/* Left column - Form content (2 columns on desktop) */}
          <div className="col-span-3 lg:col-span-2 items-center justify-center p-4">
            <h2>
              <span className="white">Request a </span>{" "}
              <span className="whitespace-nowrap">Darknet Report</span>
            </h2>
            <h3>ONE TIME FEE $200 | REPORT READY IN 24H</h3>
            <p className="mb-6">
              Unlock insights into your digital footprint within the darknet and
              deepweb. This analysis includes exposed credentials, leak trend
              analysis, most risky users, potentially spoofed domains and data
              listed for sale on darknet forums. Additionally, it covers exposed
              infrastructure exposure found in Shodan and similar platforms.The report is delivered in PDF accompanied by raw data in CSV files. The report may include unredacted login credentials
              belonging to employees, partners, or clients connected to your
              infrastructure, as well as those related to third-party servers.
            </p>
            <RequestReportForm />
            <h6 className="mb-10 hidden">
              Type your domain to get your dark web report.
            </h6>
          </div>

          {/* Right column - Image (1 column on desktop) */}
          <div className="col-span-3 lg:col-span-1 items-center justify-center p-4">

            <img
              src={require("assets/images/home/darknet-report.png")}
              className="w-full h-full"
              alt="Security Trends Analysis"
            />

          </div>
        </div>
      </Section>

      <Section style="free-trial-cta px-4 lg:px-20">
        <div className="grid grid-cols-12">
          <div className="col-span-12">
            <a className="free-trial-banner" href="/free-trial">
              <div className="label">
                <img
                  src={require("assets/images/home/latest-news-icon.png")}
                  className="img-fluid"
                  alt=""
                />
                <h3>FREE TRIAL</h3>
              </div>
              <div className="line"></div>
              <div className="cta-text">
                Start Your 7-Day Free Trial and Discover DarknetSearch in Action 
                <i className="bi bi-chevron-right"></i>
              </div>
              <div className="free-trial-btn">Start your free trial now</div>
            </a>
          </div>
        </div>
      </Section>

      <Section style="whatmonitor">
        <div className="grid grid-cols-1 lg:grid-cols-2 flex items-center gap-x-6 px-4 lg:px-20">
          <div className="col-span-1">
            <h6>WHAT SETS US APART</h6>
            <h2>
              {" "}
              <img
                src={require("assets/images/leftdot.png")}
                className="img-fluid left"
                alt=""
              />
              <span>Continous Monitoring of </span> <br />
              Open, Deep and Dark Web Sources to
            </h2>
            <h5> Identify Threats</h5>
            <p>
              GreyMatter Digital Risk Protection (DRP) detects digital risks
              that organizations care about, unparalleled collection and a
              proven threat model that adapts to your organization’s profile and
              appetite.
            </p>
          </div>
          <div className="col-span-1">
            <div className="grid grid-cols-1 lg:grid-cols-2">
              <div className="border border-black/20 border-[1px] rounded-[5px] m-[5px] pr-2 pt-4">
                {loadingStatsData ? (
                  <div className="flex justify-center py-16">
                    <FadeLoader />
                  </div>
                ) : (
                  formatChartLeaks() && (
                    <ReactApexChart
                      options={formatChartLeaks().options}
                      series={formatChartLeaks().series}
                      type="area"
                      height={350}
                    />
                  )
                )}
              </div>
              <div className="border border-black/20 border-[1px] rounded-[5px] m-[5px] pr-2 pt-4">
                {loadingStatsData ? (
                  <div className="flex justify-center py-16">
                    <FadeLoader />
                  </div>
                ) : (
                  formatChartAccounts() && (
                    <ReactApexChart
                      options={formatChartAccounts().options}
                      series={formatChartAccounts().series}
                      type="area"
                      height={350}
                    />
                  )
                )}
              </div>
            </div>

            {loadingStatsData ? (
              <div className="flex justify-center py-16">
                <FadeLoader />
              </div>
            ) : (
              !_.isNull(allStats) && (
                <div className="grid grid-cols-1 lg:grid-cols-2">
                  {bots.map((item, index) => {
                    const Wrapper = item.link ? "a" : "div";
                    const wrapperProps = item.link ? { href: item.link } : {};
                    return (
                      <Wrapper key={index} className="box" {...wrapperProps}>
                        {item.icon}
                        <h3>{allStats[item.id]?.toLocaleString()}</h3>
                        <h4>{item.title}</h4>
                        <p>{item.description}</p>
                      </Wrapper>
                    );
                  })}
                </div>
              )
            )}
          </div>

          <div className="">
            {loadingStatsData ? (
              <div className="flex justify-center py-16">
                <FadeLoader />
              </div>
            ) : (
              !_.isNull(allStats) && (
                <div className="grid grid-cols-1 lg:grid-cols-2">
                  {webblogs.map((item, index) => {
                    const Wrapper = item.link ? "a" : "div";
                    const wrapperProps = item.link ? { href: item.link } : {};
                    return (
                      <Wrapper key={index} className="box" {...wrapperProps}>
                        {item.icon}
                        <h3>{allStats[item.id]?.toLocaleString()}</h3>
                        <h4>{item.title}</h4>
                        <p>{item.description}</p>
                      </Wrapper>
                    );
                  })}
                </div>
              )
            )}
          </div>
          <div className="h-full">
            {loadingStatsData ? (
              <div className="flex justify-center py-16">
                <FadeLoader />
              </div>
            ) : (
              !_.isNull(allStats) && (
                <div className="grid grid-cols-1 lg:grid-cols-2 h-full">
                  {cc_pastes.map((item, index) => {
                    const Wrapper = item.link ? "a" : "div";
                    const wrapperProps = item.link ? { href: item.link } : {};
                    return (
                      <Wrapper key={index} className="box" {...wrapperProps}>
                        {item.icon}
                        <h3>{allStats[item.id]?.toLocaleString()}</h3>
                        <h4>{item.title}</h4>
                        <p>{item.description}</p>
                      </Wrapper>
                    );
                  })}
                </div>
              )
            )}
          </div>
        </div>
      </Section>

      <Section style="latest-leaks">
        <div
          className="grid grid-cols-1 px-4 lg:px-20"
          id="latest-leaks-added-to-kaduu"
        >
          <div className="">
            <h2>
              <img
                src={require("assets/images/leftdot.png")}
                className="img-fluid left"
                alt=""
              />{" "}
              <span>Latest Leaks</span> Added to Kaduu
              <p className="smaller pb-5">Track newly discovered credential leaks and corporate data exposures monitored across the <a className="primary-color" href="/what-is-dark-web">dark web</a> in near real time.</p>
            </h2>

            <table>
              <thead>
                <tr>
                  <th>Created</th>
                  <th>Name</th>
                  <th>Size</th>
                  <th>Records</th>
                  <th>Accounts</th>
                  <th>Similarity</th>
                </tr>
              </thead>
              <tbody>
                {loadingStatsData ? (
                  <tr>
                    <td colSpan={6}>
                      <div className="flex justify-center py-16">
                        <FadeLoader />
                      </div>
                    </td>
                  </tr>
                ) : (
                  !_.isNull(latestLeaks) &&
                  !_.isEmpty(latestLeaks) &&
                  latestLeaks
                    .slice(
                      (currentPage - 1) * rowsPerPage,
                      currentPage * rowsPerPage
                    )
                    .map((item, index) => {
                      return (
                        <tr key={index}>
                          <td data-column="Created">{item.createdAt}</td>
                          <td data-column="Name">
                            <span className="break-all">{item.name}</span>
                          </td>
                          <td data-column="Size">
                            {formatNumber(item.size ?? 0)}
                          </td>
                          <td data-column="Records">
                            {item.numberOfRecords?.toLocaleString()}
                          </td>
                          <td data-column="Accounts">
                            {item.numberOfAccounts?.toLocaleString()}
                          </td>
                          <td data-column="Similarity">
                            {item.similarity.toFixed(2)}%
                          </td>
                        </tr>
                      );
                    })
                )}
              </tbody>
            </table>

            {/* Custom Paginator */}
            {!_.isNull(latestLeaks) && !_.isEmpty(latestLeaks) && (
              <div style={{ marginTop: "20px", textAlign: "right" }}>
                {/* Page numbers */}
                {Array.from({ length: totalPages }, (_, index) => (
                  <button
                    key={index + 1}
                    onClick={() => goToPage(index + 1)}
                    disabled={currentPage === index + 1}
                    style={{
                      marginRight: "5px",
                      backgroundColor:
                        currentPage === index + 1 ? "#000000" : "#f8cf3a",
                      borderColor:
                        currentPage === index + 1 ? "#000000" : "#f8cf3a",
                      color: currentPage === index + 1 ? "#f8cf3a" : "#000000",
                    }}
                  >
                    {index + 1}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </Section>

      <Section style="latest-blog">
        <div
          className="grid grid-cols-1 px-4 lg:px-20"
        >
          <div>
            <h2>
              <img
                src={require("assets/images/leftdot.png")}
                className="img-fluid left"
                alt=""
              />{" "}
              <span>Latest Articles</span> Added to Blog
              <p className="smaller pb-5">Discover what’s new in cyber threats, <a className="primary-color" href="https://darknetsearch.com/glossary/what-is-a-data-breach/">data breaches</a> and dark web trends.</p>
            </h2>
            <HeadlessNews
              isCompact
              lang="en"
              useWpPrimaryCategory
              showExcerptTooltip={false}
              primaryCategoryLabel="Data Breach"
              enableFlip
            />
          </div>
        </div>
      </Section>
    </div>
  );
}

const ratings = [
  {
    link: "https://www.provenexpert.com/kaduu/",
    image: "home/k1.png",
    title: "ProvenExpert Rating",
  },
  {
    link: "https://www.gartner.com/reviews/vendor/kaduu/",
    image: "home/k4.png",
    title: "Gartner Rating",
  },
  {
    link: "https://www.g2.com/products/kaduu/reviews/",
    image: "home/k2.png",
    title: "G2 Rating",
  },
  {
    link: "https://www.capterra.com/p/265169/Kaduu/",
    image: "home/k3.png",
    title: "Captera Rating",
  },
];

const heroFeatureItems = [
  {
    icon: ShieldLock,
    text: "Real compromised credentials",
  },
  {
    icon: Activity,
    text: "Real-time alerts",
  },
  {
    icon: Clipboard2Check,
    text: "Automated reports",
  },
  {
    icon: Globe,
    text: "Attack surface monitoring",
  },
  {
    icon: CardList,
    text: "Risk score & benchmarks",
  },
  {
    icon: JournalBookmark,
    text: "Multi-tenant & white-label ready",
  },
];

const cardInfos = [
  {
    title: "Find exposed data in the Dark & Deep Web",
    imageTitle: "Find exposed data in the Dark & Deep Web",
    image: "home/p1.png",
    text: `Monitor the Dark and Deep Web ensuring that your organization's assets are not present on Dark Web forums, Telegram, Discord, onion sites, I2P networks, and paste sites is crucial for detecting potential threats. By actively scanning these platforms, you can prevent data leaks and other incidents that might harm your organization. Our services include searching for compromised credentials of your employees and customers, finding credit card, PII data and IoC’s for sale, identifying sensitive data in URL shorteners, detecting exposed data on public cloud storage platforms (AWS, Azure) and publicly available code repositories.`,
    link: "/solution/data-breach-detection",
  },
  {
    title:
      "Domain typo squatting & Brand Protection - Stop threat actors from spoofing your brand",
    imageTitle: "Domain typo squatting & Brand Protection",
    image: "home/p2.png",
    text: `Protect your brand from impersonation on phishing sites, app stores (Apple & Google play store), and lookalike domains. Detect malicious website that use your brand before they go live. For all discovered domains we find key properties such as WHOIS, geolocation, open web services, screenshots, logo spoofing (visual analysis), Typo-squatting techniques, web risk score & common DNS records. We can actively monitor any changes to websites and daily fetch new domain registrations (ccTLDs, gTLDs, uTLD, sTLD) and domains discovered in SSL transparency logs.`,
    link: "/solution/domain-spoofing-detection",
  },
  {
    title: "Attack Surface Discovery - Gain an attacker’s viewpoint",
    imageTitle: "Attack Surface Discovery",
    image: "home/p3.png",
    text: `It’s hard to keep track of new assets that are constantly being added to your infrastructure by developers, external agencies or freelancers. You cant run vulnerabilities scan on the assets you are unaware of. Our Attack Surface Monitoring & Management provides automatic and full visibility into your digital presence. Detect a wide range of exposures, including high risk CVEs, misconfigurations, Unsecure ports and internal systems.`,
    link: "/solution/attack-surface-discover",
  },
  {
    title:
      "Ransomware Monitoring: Safeguard Your Supply Chain from known Threat Actors",
    imageTitle: "Ransomware Monitoring",
    image: "home/p4.png",
    text: `Protect your enterprise by monitoring for potential ransomware threats within your supply chain and partner network. Many successful cyberattacks originate through third-party partners and suppliers. Our comprehensive monitoring tools help detect and mitigate risks associated with key ransomware groups like LockBit, Cl0p, Alphv, and Black Basta, among others. Stay informed about exposures and safeguard your critical assets and access points within the digital supply chain.`,
    link: "/solution/attack-surface-discover",
  },
  {
    title: "Live Darknet Investigations",
    imageTitle: "Live Darknet Investigations",
    image: "home/p5.png",
    text: `Conduct real-time searches across the darknet using our proxy services to determine if your assets are mentioned on Tor or I2P sites, deep-web forums, or within Telegram and Discord channels. Our advanced bots and search functions operate discreetly, ensuring that your queries remain invisible to other channel members.`,
    link: "/solution/data-breach-detection",
  },
];

const trusts = [
  {
    link: "https://www.defit.dk/",
    image: "home/client-1.png",
  },
  {
    link: "https://www.cic.ch/en/",
    image: "home/client-2.png",
  },
  {
    link: "https://www.infosec.ch/",
    image: "home/client-3.png",
  },
  {
    link: "https://www.infoguard.ch/en/",
    image: "home/client-4.png",
  },
  {
    link: "https://cyberpeaceinstitute.org/",
    image: "home/client-5.png",
  },
  {
    link: "https://cloudyole.es/",
    image: "home/add-1.png",
  },
  {
    link: "https://www.palladiumhotelgroup.com/es",
    image: "home/add-4.png",
  },
  {
    link: "https://www.versia.com/",
    image: "home/add-2.png",
  },
];

const homeTestimonials = [
  {
    text: `Cloud y Olé uses Kaduu’s DarknetSearch to detect client data leaks, exposed assets and domain spoofing in real time.
    The platform’s alerts and API reporting help their SOC and security teams respond faster and strengthen protection.
    They recommend it to MSSPs and tech companies needing reliable, automated threat intelligence.`,
    name: "Cloud y Ole",
    title: "Salvador Madrid ",
    company: "Chief Technology Officer",
    link: "https://darknetsearch.com/customer-success-stories/case-study/cloud-y-ole-spain-cybersecurity",
    logo: "home/cloudyole.png",
  },
  {
    text: `Bühler, a major Swiss industrial company, adopted DarknetSearch to detect leaked credentials, exposed assets and data breaches across the dark web. After integration, they feed alerts into their SIEM and security workflows so they can notify affected employees and proactively rotate credentials. They highlight the platform’s cost-effectiveness, API-driven automation and its value for threat intelligence in large enterprises.`,
    name: "Bühler",
    title: "Patrick Zimmermann",
    company: "Expert Information Security Specialist",
    link: "https://darknetsearch.com/customer-success-stories/case-study/buhler-switzerland-industrial/",
    logo: "home/buhler.png",
  },
  {
    text: `The CyberPeace Institute is a Swiss NGO protecting vulnerable communities, uses DarknetSearch to monitor the dark web for leaks, exposed credentials, and data about NGOs they support.
Thanks to the platform’s clear alerts and API integration, their volunteers quickly turn findings into protective actions for nonprofit organizations.
They value the tool’s ease of use, relevance of intelligence, and ability to deliver cybersecurity support despite limited resources.`,
    name: "CyberPeace Institute",
    title: "Fabien Leimgruber",
    company: "Head of Cyber Resilience",
    link: "https://darknetsearch.com/customer-success-stories/case-study/cyberpeace-institute-switzerland-ngo/",
    logo: "home/cyberpeace.png",
  },
];

const homeUseCases = [
  {
    role: "By Role",
    title: "CISOs (Chief Information Security Officers)",
    challenge:
      "Strategic visibility, risk prioritization, and executive accountability across the organization.",
    useCase:
      "Gain continuous visibility into leaked credentials, exposed access, and third-party breaches across the dark web. Identify early-stage risks, track external exposure beyond the perimeter, and translate complex threat intelligence into clear, board-ready insights for governance, risk, and compliance.",
    outcome:
      "Reduce unknown exposure before it escalates into a business, regulatory, or reputational incident.",
    keywords:
      "dark web monitoring, executive risk visibility, CISO threat intelligence, third-party risk, credential exposure",
    link: "/use-cases/ciso",
    cta: {
      label: "View executive risk reports",
      href: "https://darknetsearch.com/pricing",
    },
    icon: <ShieldLock size={26} />,
  },
  {
    role: "By Role",
    title: "Threat Intelligence Analysts",
    challenge:
      "Detecting, correlating, and contextualizing emerging threats across fragmented external sources.",
    useCase:
      "Unify dark web search, stolen data monitoring, malware and stealer log analysis, and threat actor profiling in a single workflow. Correlate indicators, map activity to known TTPs, and enrich investigations with external intelligence before threats surface internally.",
    outcome:
      "Identify and validate emerging threats earlier, with higher confidence and less noise.",
    keywords:
      "threat intelligence platform, dark web search, TTPs, threat actor analysis, stealer logs",
    cta: {
      label: "Explore live threat signals",
      href: "https://darknetsearch.com/pricing",
    },
    link: "/use-cases/threat",
    icon: <Radar size={26} />,
  },
  {
    role: "By Role",
    title: "Security Practitioners & SOC Analysts",
    challenge:
      "Fast and accurate incident detection, investigation, and response under constant alert pressure.",
    useCase:
      "Enrich SIEM and incident investigations with external dark web signals tied to real-world threats. Correlate internal alerts with leaked credentials, stealer logs, and malware-related data to confirm incidents faster, reduce false positives, and prioritize response actions.",
    outcome:
      "Accelerate investigations and reduce mean time to respond (MTTR) by validating alerts with external intelligence.",
    keywords:
      "SOC operations, incident response, credential leaks, malware detection, SIEM enrichment",
    cta: {
      label: "Investigate active incidents",
      href: "https://darknetsearch.com/pricing",
    },
    link: "/use-cases/soc",
    icon: <Activity size={26} />,
  },
];

const bots = [
  {
    id: "botRecords",
    icon: <JournalBookmark size={24} />,
    title: "Bot Records",
    description: "Records extracted from botnet logs.",
    link: "/botnet-records",
  },
  {
    id: "bots",
    icon: <Robot size={24} />,
    title: "Bots",
    description:
      "Separate bot installations indexed extracted from botnet logs.",
    link: "/compromised-bots",
  },
];

const cc_pastes = [
  {
    id: "creditCards",
    icon: <CreditCard size={24} />,
    title: "Credit Cards",
    description: "Credit cards automatically extracted from leaked files.",
    link: "/credit-card-leaks",
  },
  {
    id: "pastes",
    icon: <Clipboard2Check size={24} />,
    title: "Pastes",
    description:
      "Text files posted on pastebin-like websites and indexed by Kaduu crawlers.",
    link: "/pastebin-leaks",
  },
];
const webblogs = [
  {
    id: "leakRecords",
    icon: <CardList size={24} />,
    title: "Overall Records",
    description:
      "Data records or text file lines, which do not necessarily represent a leaked account. It can be an arbitrary line of a leaked Excel file or one row from an SQL dump of a database table.",
    link: "/data-records",
  },
  {
    id: "leakWebsites",
    icon: <Globe size={24} />,
    title: "Websites",
    description:
      "Unique breached websites that were detected when extracting accounts from leak files. Every website tracked here contains leaked login data.",
    link: "/breached-websites",
  },
];

const RequestReportForm = () => {
  const formRef = useRef();
  const { enqueueSnackbar } = useSnackbar();

  const checkMatchDomain = (value, domain) => {
    try {
      const emailDomain = value.split("@")[1];
      return emailDomain === domain;
    } catch (err) {
      return false;
    }
  };

  function validateDomain(value) {
    let error;
    if (!value) {
      error = "Domain is required";
    } else if (value.length > 253) {
      error = "Domain must be at most 253 characters";
    } else if (
      !/^(?!https?:\/\/)[a-zA-Z0-9-]+\.[a-zA-Z]{2,}(\.[a-zA-Z]{2})?$/.test(
        value
      )
    ) {
      error = "Invalid domain format";
    }
    return error;
  }

  function validateUserName(value) {
    let error;

    value = value ?? "";
    if (!value.trim()) {
      return "User name is required.";
    }

    const nameParts = value.trim().split(" ");
    if (nameParts.length < 2) {
      return "Please enter at least first and last name.";
    }
    const nameRegex = /^[a-zA-Z\s]+$/;
    if (!nameRegex.test(value)) {
      return "User name can only contain letters and spaces.";
    }

    return error;
  }

  function validateEmail(value) {
    const domain = formRef.current.values.domain;

    let error;
    if (!value) {
      error = "Business email is required";
    } else if (
      !/^[a-zA-Z0-9.!#$%&'*+\/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/.test(
        value
      )
    ) {
      error = "Invalid email address!";
    } else if (!checkPrivateEmail(value)) {
      error = "Only business email required";
    } else if (!checkMatchDomain(value, domain)) {
      error = "The domain and email's domain must match.";
    }

    return error;
  }

  function validateChecked(value) {
    let error;
    if (!value) {
      error = "You need to have this authorization";
    }

    return error;
  }

  const [loading, setLoading] = useState(false);

  return (
    <Formik
      innerRef={formRef}
      initialValues={{
        domain: "",
        email: "",
        username: "",
        checked: false,
        ...createAntiBotInitialValues(),
      }}
      onSubmit={async (values, { resetForm }) => {
        setLoading(true);

        const result = await sendEmail(
          values.email,
          {
            subject: "Darknet-report request",
            message: `Title: Darknet-Report request\r\n\r\nUser Name: ${values.username}\r\nUser Email: ${values.email}\r\nDomain: ${values.domain}`,
          },
          enqueueSnackbar,
          {
            type: "darknet_report_request",
            name: values.username,
          },
          getAntiBotPayload(values)
        );

        if (result) resetForm();
        setLoading(false);
      }}
    >
      {({ errors, validateForm, handleSubmit }) => (
        <Form>
          <FormBotProtection />
          <div className="grid grid-cols-2 md:grid-cols-3">
            <div className="col-span-2">
              <div className="form mt-4">
                <Field
                  name="domain"
                  type="text"
                  validate={validateDomain}
                  placeholder="Your domain (e.g. example.com)"
                  disabled={loading}
                />
              </div>
              {errors.domain ? (
                <div className="formik-error">{errors.domain}</div>
              ) : null}

              <div className="form mt-4">
                <Field
                  name="email"
                  type="email"
                  validate={validateEmail}
                  placeholder="Business Email"
                  disabled={loading}
                />
              </div>
              {errors.email ? (
                <div className="formik-error">{errors.email}</div>
              ) : null}

              <div className="form mt-4">
                <Field
                  name="username"
                  type="text"
                  validate={validateUserName}
                  placeholder="Your fullname (e.g. John Doe)"
                  disabled={loading}
                />
              </div>
              {errors.username ? (
                <div className="formik-error">{errors.username}</div>
              ) : null}
            </div>
            <div className="col-span-2 md:col-span-1">
              <div className="form mt-4">
                <button
                  type="button"
                  className={loading ? "loading" : ""}
                  disabled={loading}
                  onClick={() =>
                    validateForm().then((result) => {
                      if (_.isEmpty(result)) handleSubmit();
                    })
                  }
                >
                  <div className="flex items-center">
                    <span>REQUEST REPORT</span>
                    <div
                      className="formik-loading ms-6 me-[-12px] "
                      style={{ display: loading ? "inline" : "none" }}
                    >
                      <FadeLoader
                        height={8}
                        margin={-8}
                        radius={3}
                        width={3}
                        cssOverride={{ top: 19 }}
                      />
                    </div>
                  </div>
                </button>
              </div>
              <div className="mt-4">
                <label className="text-white">
                  <Field
                    name="checked"
                    type="checkbox"
                    className="custom-checkbox"
                    validate={validateChecked}
                  />
                  &nbsp;&nbsp;I have the authorization to review sensitive and
                  security-related data belonging to my organization.
                </label>
              </div>
              {errors.checked ? (
                <div className="formik-error">{errors.checked}</div>
              ) : null}


            </div>
          </div>
        </Form>
      )}
    </Formik>
  );
};

export default Home;
